import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const PostCampaign: React.FC = () => {
  const [caption, setCaption] = useState('');
  // ✅ FIX 1: NO default value for privacy (TikTok requirement)
  const [visibility, setVisibility] = useState('');
  const [isPrivacyDropdownOpen, setIsPrivacyDropdownOpen] = useState(false);
  const privacyDropdownRef = useRef<HTMLDivElement>(null);
  // ✅ FIX 2: All interaction controls default to FALSE (TikTok requirement)
  const [allowComment, setAllowComment] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  const [discloseContent, setDiscloseContent] = useState(false);
  const [brandType, setBrandType] = useState('your-brand');
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (privacyDropdownRef.current && !privacyDropdownRef.current.contains(event.target as Node)) {
        setIsPrivacyDropdownOpen(false);
      }
    };

    if (isPrivacyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPrivacyDropdownOpen]);

  // Video URL - can be passed as prop or from state
  const videoUrl = 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/video/1775771504869-flyer_video_1775771504821.mp4';

  // ✅ FIX 5: Add validation
  const canUpload = caption.trim() !== '' && visibility !== '';

  const handleUpload = () => {
    if (!canUpload) return;
    
    setIsUploading(true);
    
    // Show uploading for 4 seconds
    setTimeout(() => {
      setIsUploading(false);
      setShowSuccessAlert(true);
      
      // Auto-hide alert after 5 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 5000);
    }, 4000);
  };
  
  const videoData = {
    filename: 'flyer_video_1775771504263',
    format: 'MP4',
    resolution: '1080P',
    size: '1.66MB',
    duration: '00:8',
    currentTime: '00:01'
  };

  return (
    <div className="min-h-screen bg-[#121212] text-white p-6">
      {/* Success Alert - Top Right */}
      {showSuccessAlert && (
        <motion.div
          initial={{ opacity: 0, y: -50, x: 50 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-6 right-6 z-50"
        >
          <div className="bg-green-500 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 min-w-[300px]">
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <p className="font-semibold">Success!</p>
              <p className="text-sm opacity-90">Posted to TikTok</p>
            </div>
          </div>
        </motion.div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Side - Video Preview */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Video Player */}
            <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
              {/* Actual Video Element */}
              <video
                src={videoUrl}
                controls
                className="w-full h-full object-contain"
                poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23000' width='100' height='100'/%3E%3C/svg%3E"
              >
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#1e1e1e] rounded-xl p-4 space-y-4">
              {/* Filename - Full Row */}
              <div>
                <p className="text-xs text-gray-400 mb-1">Filename</p>
                <p className="text-sm font-medium">{videoData.filename}</p>
              </div>
              
              {/* Other Properties - Second Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Format</p>
                  <p className="text-sm font-medium">{videoData.format}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Resolution</p>
                  <p className="text-sm font-medium">{videoData.resolution}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Size</p>
                  <p className="text-sm font-medium">{videoData.size}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Upload Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-800">
              <h2 className="text-lg font-semibold">Upload to TikTok</h2>
            </div>

            {/* ✅ FIX 1: Account Selector - MUST SHOW (TikTok requirement) */}
            <div className="bg-[#1e1e1e] rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-2">Posting as</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#ee4423] flex items-center justify-center">
                  <span className="text-sm font-bold">S</span>
                </div>
                <span className="font-medium">@srinivasanvenkat3</span>
              </div>
            </div>

            {/* Caption */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Caption <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a title that describes your video"
                  className="w-full bg-[#1e1e1e] border border-gray-700 rounded-xl p-3 pr-16 resize-none focus:outline-none focus:border-orange-500 transition-colors"
                  rows={3}
                  maxLength={100}
                />
                <span className="absolute bottom-3 right-3 text-xs text-gray-500">
                  {caption.length}/100
                </span>
              </div>
            </div>

            {/* ✅ FIX 2: Custom Privacy Dropdown - NO default value, user MUST select */}
            <div className="relative" ref={privacyDropdownRef}>
              <label className="block text-sm font-medium mb-2">
                Who can view this video <span className="text-red-500">*</span>
              </label>
              
              {/* Custom Dropdown Trigger */}
              <button
                type="button"
                onClick={() => setIsPrivacyDropdownOpen(!isPrivacyDropdownOpen)}
                className={`w-full bg-[#1e1e1e] border rounded-xl p-3 flex items-center justify-between transition-colors ${
                  visibility === '' ? 'border-red-500 text-gray-500' : 'border-gray-700 text-white'
                }`}
              >
                <span>{visibility || 'Select privacy level'}</span>
                <svg 
                  className={`w-5 h-5 transition-transform ${isPrivacyDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Custom Dropdown Menu */}
              {isPrivacyDropdownOpen && (
                <div className="absolute z-10 w-full mt-2 bg-[#2a2a2a] border border-gray-600 rounded-xl shadow-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setVisibility('Public');
                      setIsPrivacyDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#3a3a3a] transition-colors flex items-center gap-3"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-medium">Public</p>
                      <p className="text-xs text-gray-400">Everyone can watch your video</p>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setVisibility('Friends');
                      setIsPrivacyDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#3a3a3a] transition-colors flex items-center gap-3 border-t border-gray-700"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <div>
                      <p className="font-medium">Friends</p>
                      <p className="text-xs text-gray-400">Only your friends can watch</p>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setVisibility('Private');
                      setIsPrivacyDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#3a3a3a] transition-colors flex items-center gap-3 border-t border-gray-700"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <div>
                      <p className="font-medium">Private</p>
                      <p className="text-xs text-gray-400">Only you can watch</p>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* ✅ FIX 3: Allow users to - All default to OFF (TikTok requirement) */}
            <div>
              <label className="block text-sm font-medium mb-3">Allow users to</label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowComment}
                    onChange={(e) => setAllowComment(e.target.checked)}
                    className="w-5 h-5 rounded bg-[#1e1e1e] border-2 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
                  />
                  <span className="text-sm">Comment</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowDuet}
                    onChange={(e) => setAllowDuet(e.target.checked)}
                    className="w-5 h-5 rounded bg-[#1e1e1e] border-2 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
                  />
                  <span className="text-sm">Duet</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowStitch}
                    onChange={(e) => setAllowStitch(e.target.checked)}
                    className="w-5 h-5 rounded bg-[#1e1e1e] border-2 border-gray-700 checked:bg-orange-500 checked:border-orange-500 cursor-pointer"
                  />
                  <span className="text-sm">Stitch</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">All interaction controls are disabled by default. Enable as needed.</p>
            </div>

            {/* Disclose video content */}
            <div className="border-t border-gray-800 pt-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium mb-1">Disclose video content</h3>
                  <p className="text-xs text-gray-400">
                    Turn on to disclose that this video promotes goods or services in exchange for something of value.
                  </p>
                </div>
                <button
                  onClick={() => setDiscloseContent(!discloseContent)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    discloseContent ? 'bg-orange-500' : 'bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      discloseContent ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Brand Type Selection */}
              {discloseContent && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-3 mt-4"
                >
                  {/* Info Box */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
                    <p className="text-xs text-blue-300">
                      Your video will be labeled "Promotional content". This cannot be changed once your video is posted.
                    </p>
                  </div>

                  <p className="text-sm text-gray-400">
                    Turn on to disclose that this video promotes goods or services in exchange for something of value. Your video could promote yourself, a third party, or both.
                  </p>

                  {/* Your brand */}
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-[#1e1e1e] transition-colors">
                    <input
                      type="radio"
                      name="brandType"
                      value="your-brand"
                      checked={brandType === 'your-brand'}
                      onChange={(e) => setBrandType(e.target.value)}
                      className="mt-1 w-5 h-5 text-orange-500"
                    />
                    <div>
                      <p className="font-medium mb-1">Your brand</p>
                      <p className="text-xs text-gray-400">
                        You are promoting yourself or your own business. This video will be classified as Brand Organic.
                      </p>
                    </div>
                  </label>

                  {/* Branded content */}
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-[#1e1e1e] transition-colors">
                    <input
                      type="radio"
                      name="brandType"
                      value="branded-content"
                      checked={brandType === 'branded-content'}
                      onChange={(e) => setBrandType(e.target.value)}
                      className="mt-1 w-5 h-5 text-orange-500"
                    />
                    <div>
                      <p className="font-medium mb-1">Branded content</p>
                      <p className="text-xs text-gray-400">
                        You are promoting another brand or a third party. This video will be classified as Branded Content.
                      </p>
                    </div>
                  </label>
                </motion.div>
              )}
            </div>

            {/* ✅ FIX 4: Music Usage Consent - MUST SHOW ALWAYS BEFORE UPLOAD (TikTok requirement) */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-sm text-yellow-200">
                By posting, you agree to TikTok's{' '}
                <a 
                  href="https://www.tiktok.com/legal/music-usage-confirmation" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:underline font-medium"
                >
                  Music Usage Confirmation
                </a>
              </p>
            </div>

            {/* Upload Button - with validation */}
            <button 
              onClick={handleUpload}
              disabled={isUploading || !canUpload}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {isUploading && (
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>

            {/* Validation Message */}
            {!canUpload && (
              <p className="text-xs text-red-400 text-center -mt-4">
                Please fill in all required fields (Caption and Privacy)
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PostCampaign;
